import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { EventTypes } from 'src/app/core/models/events.model';
import { EventService } from 'src/app/core/services/event.service';

export interface FileValidationError {
  code: string;
  message: string;
}

export interface FileValidationResult {
  valid: boolean;
  errors: FileValidationError[];
  warnings: FileValidationError[];
}

@Component({
  selector: 'app-input-upload-file',
  templateUrl: './input-upload-file.component.html',
  styleUrls: ['./input-upload-file.component.scss'],
})
export class InputUploadFileComponent implements OnInit {
  @Input() fileSrc: string = '';
  @Input() maxHeight: string = 'auto';
  @Input() hasTitleDeleteImg: boolean = true;
  @Input() deleteButtonDisposition: 'vertical' | 'horizontal' = 'horizontal';
  @Input() enablePdfPreview = false;
  @Input() maxFileSizeMB: number = 10;
  @Input() acceptedFormats: string = 'image/jpeg,image/png,application/pdf';
  @Input() label: string = '';
  @Input() title: string = '';
  @Input() showValidationMessages: boolean = true;
  @Input() enableCamera: boolean = true; // NEW: Enable camera capture option

  @Output() file: EventEmitter<File | null> = new EventEmitter<File | null>();
  @Output() validationError: EventEmitter<FileValidationResult> =
    new EventEmitter<FileValidationResult>();
  @Output() photoButtonClick: EventEmitter<void> = new EventEmitter<void>(); // NEW: Emitted when user clicks to take photo
  @Output() uploadButtonClick: EventEmitter<void> = new EventEmitter<void>(); // NEW: Emitted when user clicks to upload file

  @ViewChild('inputfile', { static: false })
  input!: ElementRef<HTMLInputElement>;

  isPdf = false;
  isUploading = false;
  isDragging = false;
  currentFile: File | null = null;
  validationResult: FileValidationResult | null = null;

  // NEW: Camera-related properties
  showCamera: boolean = false;
  hasCameraSupport: boolean = false;
  isCheckingCamera: boolean = true; // NEW: Loading state for camera check

  constructor(
    private translate: TranslateService,
    private eventService: EventService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    console.log('InputUploadFile: ngOnInit called');
    console.log('InputUploadFile: enableCamera =', this.enableCamera);

    const startTime = Date.now();

    this.sendEventOnLoadPage();

    // Check camera support immediately
    this.checkCameraSupport().then(() => {
      const elapsed = Date.now() - startTime;
      console.log(`InputUploadFile: checkCameraSupport completed in ${elapsed}ms`);
    });
  }

  private async sendEventOnLoadPage(): Promise<any> {

    const eventUploadDocument: EventTypes = EventTypes.KYC_DOC_SENDING_LOAD_PAGE;
    const claimId = this.activatedRoute.snapshot.paramMap.get('claimId');

    try{
      await this.eventService.sendEventWithCaseId(eventUploadDocument, claimId);
    }catch(error) {
      console.error('Event not sended, ', error);
    }
  }

  // NEW: Check if device has camera support
  private async checkCameraSupport() {
    const startTime = Date.now();
    console.log('InputUploadFile: checkCameraSupport started');

    if (!this.enableCamera) {
      console.log('InputUploadFile: Camera disabled by config');
      this.hasCameraSupport = false;
      this.isCheckingCamera = false;
      return;
    }

    try {
      console.log('InputUploadFile: Checking MediaDevices API...');

      const hasMediaDevices = !!(
        navigator.mediaDevices && navigator.mediaDevices.getUserMedia
      );

      const apiCheckTime = Date.now() - startTime;
      console.log(`InputUploadFile: API check completed in ${apiCheckTime}ms`);
      console.log('InputUploadFile: Has MediaDevices API:', hasMediaDevices);

      if (hasMediaDevices) {
        // Just check if API exists, don't request permission yet
        this.hasCameraSupport = true;
        console.log('InputUploadFile: Camera support enabled');
      } else {
        this.hasCameraSupport = false;
        console.log('InputUploadFile: Camera support not available');
      }
    } catch (error) {
      console.warn('InputUploadFile: Camera support check failed:', error);
      this.hasCameraSupport = false;
    } finally {
      const totalTime = Date.now() - startTime;
      console.log(`InputUploadFile: checkCameraSupport finished in ${totalTime}ms`);
      this.isCheckingCamera = false;
    }
  }

  // NEW: Open camera capture
  openCamera() {
    this.photoButtonClick.emit(); // Emit event to parent component
    this.showCamera = true;
  }

  // NEW: Close camera capture
  closeCamera() {
    this.showCamera = false;
  }

  // NEW: Handle photo captured from camera
  async onPhotoCaptured(file: File) {
    this.showCamera = false;
    await this.processFile(file);
  }

  private getFormatName(mimeType: string): string {
    const formatMap: Record<string, string> = {
      'image/jpeg': 'JPEG',
      'image/jpg': 'JPG',
      'image/png': 'PNG',
      'application/pdf': 'PDF',
      'image/gif': 'GIF',
      'image/webp': 'WEBP',
    };
    return formatMap[mimeType.toLowerCase()] || mimeType;
  }

  private getAcceptedTypesDisplay(): string {
    const acceptedTypes = this.acceptedFormats.split(',').map((t) => t.trim());
    const formatNames = acceptedTypes.map((type) => this.getFormatName(type));
    const orWord = this.translate.instant('or') || 'or';

    switch (formatNames.length) {
      case 0:
        return '';
      case 1:
        return formatNames[0];
      case 2:
        return `${formatNames[0]} ${orWord} ${formatNames[1]}`;
      default:
        const allButLast = formatNames.slice(0, -1).join(', ');
        const last = formatNames[formatNames.length - 1];
        return `${allButLast} ${orWord} ${last}`;
    }
  }

  async onFileChange(event: any) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      await this.processFile(file);
    }
  }

  onUploadButtonClick(): void {
    this.uploadButtonClick.emit(); // Emit event to parent component
    this.input.nativeElement.click();
  }

  async processFile(file: File) {
    this.isUploading = true;
    this.currentFile = file;

    // Validate file
    const validation = this.validateFile(file);
    this.validationResult = validation;

    if (!validation.valid) {
      this.validationError.emit(validation);
      this.isUploading = false;
      return;
    }

    // Check file type
    if (this.enablePdfPreview) {
      this.isPdf = file.type === 'application/pdf';
    }

    // Generate preview
    const reader = new FileReader();
    reader.onload = (e) => {
      this.fileSrc = e.target?.result as string;
      this.isUploading = false;
      this.file.emit(file);
    };

    reader.onerror = () => {
      this.validationResult = {
        valid: false,
        errors: [{ code: 'FILE_READ_ERROR', message: 'FailedToReadFile' }],
        warnings: [],
      };
      this.isUploading = false;
    };

    reader.readAsDataURL(file);
  }

  validateFile(file: File): FileValidationResult {
    const errors: FileValidationError[] = [];
    const warnings: FileValidationError[] = [];

    // Check file size
    const maxSizeBytes = this.maxFileSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      const message = this.translate.instant('FileTooLargeMax', {
        size: this.maxFileSizeMB,
      });
      errors.push({
        code: 'FILE_TOO_LARGE',
        message,
      });
    }

    // Check file type — normalize JPEG variants before comparing
    const acceptedTypes = this.acceptedFormats.split(',').map((t) => t.trim());
    const normalizeType = (type: string): string =>
      type === 'image/jpg' ? 'image/jpeg' : type;
    const normalizedAccepted = acceptedTypes.map(normalizeType);
    const normalizedFileType = normalizeType(file.type);
    // Accept image/heic and image/hevc when image/jpeg is accepted (iPhone photos)
    const isHeicVariant =
      normalizedAccepted.includes('image/jpeg') &&
      (file.type === 'image/heic' || file.type === 'image/hevc');

    if (!normalizedAccepted.includes(normalizedFileType) && !isHeicVariant) {
      const acceptedTypesDisplay = this.getAcceptedTypesDisplay();
      const translationKey = 'FileInvalidTypeSuggestedAction';
      const message = this.translate.instant(translationKey, {
        acceptedFormats: acceptedTypesDisplay,
      });

      errors.push({
        code: 'FILE_INVALID_TYPE',
        message: message,
      });
    }

    // Warning for large files (> 50% of max)
    if (file.size > maxSizeBytes * 0.5 && file.size <= maxSizeBytes) {
      warnings.push({
        code: 'FILE_LARGE',
        message: this.translate.instant('FileIsLargeWarning', {
          size: (file.size / 1024 / 1024).toFixed(2),
        }),
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  removeFile() {
    this.input.nativeElement.value = '';
    this.input.nativeElement.files = null;
    this.fileSrc = '';
    this.currentFile = null;
    this.validationResult = null;
    this.isPdf = false;
    this.file.emit(null);
  }

  replaceFile() {
    this.removeFile();
    this.input.nativeElement.click();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer!.dropEffect = 'copy';
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  async onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const file = event.dataTransfer.files[0];
      await this.processFile(file);
      event.dataTransfer.clearData();
    }
  }

  get expandedAcceptFormats(): string {
    const types = this.acceptedFormats.split(',').map((t) => t.trim());
    const expanded = new Set(types);
    if (expanded.has('image/jpeg')) {
      expanded.add('image/jpg');
      expanded.add('image/heic');
      expanded.add('image/hevc');
    }
    return [...expanded].join(',');
  }

  get fileSize(): string {
    if (!this.currentFile) return '';
    const sizeKB = this.currentFile.size / 1024;
    if (sizeKB < 1024) {
      return `${sizeKB.toFixed(0)} KB`;
    }
    return `${(sizeKB / 1024).toFixed(2)} MB`;
  }

  get fileName(): string {
    return this.currentFile?.name || '';
  }

  get hasErrors(): boolean {
    return (
      this.validationResult !== null && this.validationResult.errors.length > 0
    );
  }

  get hasWarnings(): boolean {
    return (
      this.validationResult !== null &&
      this.validationResult.warnings.length > 0
    );
  }
}
